// apps/vendor/src/features/orders/OrderDetailDrawer.tsx
//
// R7-2 (2026-05-17) — right-side drawer hosting OrderInfo + full ActionBar
// so vendors can mutate order state without navigating away from the list.

import { useEffect, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { ActionBar } from './detail/ActionBar.js';
import { OrderInfo } from './detail/OrderInfo.js';
import { ProductionHistoryPanel } from './detail/ProductionHistoryPanel.js';
import { TransitionTimeline } from './detail/TransitionTimeline.js';
import {
  useOrderDetail,
  useOrderTransitions,
  useProductionHistory,
} from './api.js';

interface Props {
  open: boolean;
  orderId: string | null;
  itemId: string | null;
  onClose: () => void;
}

export function OrderDetailDrawer({
  open,
  orderId,
  itemId,
  onClose,
}: Props): JSX.Element | null {
  const { t } = useTranslation('orders');
  const detailQ = useOrderDetail(orderId ?? undefined, itemId ?? undefined);
  const transitionsQ = useOrderTransitions(
    orderId ?? undefined,
    itemId ?? undefined,
  );
  const historyQ = useProductionHistory(
    orderId ?? undefined,
    itemId ?? undefined,
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !orderId || !itemId) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="order-detail-drawer"
      onClick={onClose}
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-40 flex justify-end bg-black/30"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-pw-bg shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-pw-border px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-pw-text">
              {detailQ.data?.order_summary?.order_number ?? t('drawerExtra.fallbackTitle', { suffix: orderId.slice(-8) })}
            </h2>
            <p className="text-xs text-pw-text-muted">
              {t('drawerExtra.itemMetadata', { itemSuffix: itemId.slice(-8), version: detailQ.data?.item_version ?? '?' })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/orders/${orderId}/items/${itemId}`}
              onClick={onClose}
              className="rounded-md border border-pw-border bg-pw-surface px-2.5 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
            >
              {t('drawerExtra.openFull')}
            </Link>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={onClose}
              aria-label={t('drawerExtra.closeAriaLong')}
              data-testid="order-detail-drawer-close"
            >
              {t('drawerExtra.closeShort')}
            </Button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4">
          {detailQ.isLoading ? (
            <p className="text-center text-sm text-pw-text-muted">{t('drawerExtra.loading')}</p>
          ) : detailQ.isError || !detailQ.data ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {t('drawerExtra.loadFailed', { message: detailQ.error?.message ?? '' })}
            </p>
          ) : (
            <>
              <OrderInfo detail={detailQ.data} />
              <ActionBar
                detail={detailQ.data}
                onMutated={() => {
                  void detailQ.refetch();
                  void transitionsQ.refetch();
                  void historyQ.refetch();
                }}
              />
              <ProductionHistoryPanel
                rows={historyQ.data?.rows ?? []}
                isLoading={historyQ.isLoading}
              />
              <TransitionTimeline
                rows={transitionsQ.data?.rows ?? []}
                isLoading={transitionsQ.isLoading}
              />
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default OrderDetailDrawer;
